import React, { useState, useEffect } from 'react';
import { productionAPI } from '../../api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';
import { useWindowedList } from '../../hooks/useWindowedList';

// ============ PRODUCTION PLANNING TAB ============
export const PlanningTab = ({ orders = [], products = [], refreshKey = 0, onEditOrder, restoreDrillContext, onRestoreDrillContextConsumed }) => {
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const tomorrowISO = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const productById = React.useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  // Discount map used to compute the same Net Bill amount that's shown on
  // invoices / WhatsApp / Reports. Mirrors `_order_net_total` (backend) and
  // the same helper in OrdersTab / CrmDealerDialog / DealerCRMTab.
  const productDiscountByPid = React.useMemo(() => (
    Object.fromEntries((products || []).filter(p => p && p.id).map(p => [p.id, p.discount_percent]))
  ), [products]);
  // Compute (gross, discountPct, discountAmt, net) for an order. Precedence
  // for the % is: item.discount_percent → product master → order.discount_percent → 10%.
  // We use a SINGLE blended pct so the popup mirrors the Review dialog's
  // "Dealer Discount (X%)" + "Net Payable" presentation.
  const computeOrderTotals = React.useCallback((o) => {
    const orderPct = o.discount_percent == null ? 10 : Number(o.discount_percent);
    let gross = 0, net = 0;
    (o.items || []).forEach((it) => {
      const lineGross = Number(it.total || 0);
      const pct = it.discount_percent != null
        ? Number(it.discount_percent)
        : (productDiscountByPid[it.product_id] != null ? Number(productDiscountByPid[it.product_id]) : orderPct);
      gross += lineGross;
      net += lineGross * (1 - pct / 100);
    });
    const discountAmt = gross - net;
    const blendedPct = gross > 0 ? (discountAmt / gross) * 100 : 0;
    return { gross, net, discountAmt, blendedPct };
  }, [productDiscountByPid]);

  const batchSizeFor = (pid, facility) => {
    const p = productById[pid];
    if (!p) return null;
    const s = facility === 'E-302' ? p.batch_size_e302 : p.batch_size_e303;
    return s && Number(s) > 0 ? Number(s) : null;
  };

  // Date preset (matches Product Summary): 'today' | 'tomorrow' | 'edit'
  const [datePreset, setDatePreset] = useState('today');
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [groupByParent, setGroupByParent] = useState(false);
  // Free-text product search (case-insensitive substring match on product_name
  // and any nested sub_products.name). Filters `rows` before render.
  const [productSearch, setProductSearch] = useState('');

  // Force the "Stack MISC under parent" checkbox back to UNCHECKED every
  // time the operator (re-)enters the Planning tab. The component stays
  // mounted across tab switches for performance, so without this reset
  // a once-checked state would persist forever in the session — user
  // explicitly asked for default-off on every entry (28/Jun/2026).
  useEffect(() => {
    setGroupByParent(false);
  }, [refreshKey]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState({}); // { product_id: {E-302, E-303} }
  const [expandedProduct, setExpandedProduct] = useState(null);
  // Reserved (must-fulfil) dealers per product. Key: `${product_id}::${dealer_id}` → true.
  // Reserved qty is informational: it shows what's locked for priority dealers and
  // computes a residual production target for the unreserved pool.
  const [reservedKeys, setReservedKeys] = useState({});
  // Lots count per (product_id × facility). Default 1. Triggers lot-sheet sync on change.
  // Tracks lot count per (product_id × facility). Operator-editable input.
  // Sync to the Production Planner is now MANUAL via the "Send to Production
  // Planner" button instead of debounced-on-edit.
  const [lotsByKey, setLotsByKey] = useState({});
  // Which sub-brand is expanded under a parent. Key: `${parent_id}::${sub_brand_name}`.
  const [expandedSub, setExpandedSub] = useState(null);

  // Multi-order drill dialog (mirrors Product Summary): { orders, product_id, product_name, dealer_name }
  const [drillOrders, setDrillOrders] = useState(null);

  // Restore the drill popup when returning here after editing an order via
  // OrdersTab (the AdminDashboard hand-off). We re-resolve orders against
  // the CURRENT `orders` prop so the popup reflects any edits the operator
  // just made. One-shot — `onRestoreDrillContextConsumed` clears the
  // context so subsequent renders don't pop the popup again.
  React.useEffect(() => {
    if (!restoreDrillContext) return;
    const { dealer_id, dealer_name, product_id, product_name, sub_brand_filter } = restoreDrillContext;
    if (!product_id) {
      if (onRestoreDrillContextConsumed) onRestoreDrillContextConsumed();
      return;
    }
    const matches = (orders || []).filter(o => {
      if (dealer_id && o.dealer_id !== dealer_id) return false;
      const hasProduct = (o.items || []).some(i => i.product_id === product_id || i.parent_product_id === product_id);
      return hasProduct;
    }).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    if (matches.length > 0) {
      setDrillOrders({
        orders: matches,
        product_id,
        product_name,
        dealer_name,
        sub_brand_filter,
      });
    }
    if (onRestoreDrillContextConsumed) onRestoreDrillContextConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreDrillContext, orders]);

  // Click-to-sort table state. Default: product_name asc. Clicking the active
  // column toggles asc↔desc; clicking a different column switches key + dir.
  const [sortBy, setSortBy] = useState({ key: 'product_name', dir: 'asc' });
  const toggleSort = (key, defaultDir = 'asc') => {
    setSortBy((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: defaultDir });
  };
  const sortArrow = (key) => sortBy.key !== key ? '' : (sortBy.dir === 'asc' ? ' \u2191' : ' \u2193');

  const fmtDate = (d) => d ? new Date((d.includes('T') ? d : d + 'T00:00:00')).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  const applyPreset = (key) => {
    setDatePreset(key);
    if (key === 'today') { setDateFrom(todayISO()); setDateTo(todayISO()); }
    else if (key === 'tomorrow') { setDateFrom(tomorrowISO()); setDateTo(tomorrowISO()); }
    // 'edit' keeps current values; date inputs become visible
  };

  // Tracks which (product_id × facility) inputs have a SAVED value in the
  // backend. Used to lock the input (read-only) and show an Edit pencil button.
  // Key: `${product_id}::${facility}` → true.
  const [savedKeys, setSavedKeys] = useState({});
  // Tracks which input the user has clicked "Edit" on (unlocks for editing).
  // Cleared on successful save. Key: same as above.
  const [editingKeys, setEditingKeys] = useState({});

  // Monotonic request counter — drops stale concurrent responses so the
  // latest date-range fetch always wins (race-condition guard, see StockTab
  // for the original bug report).
  const loadSeqRef = React.useRef(0);

  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const [planRes, ovRes] = await Promise.all([
        productionAPI.getPlan({ date_from: dateFrom, date_to: dateTo, group_by_parent: groupByParent }),
        // Saved overrides are keyed by the target schedule date (dateTo).
        productionAPI.getPlanOverrides(dateTo).catch(() => ({ data: { overrides: {} } })),
      ]);
      if (seq !== loadSeqRef.current) return;  // stale — drop
      setData(planRes.data);
      const savedOv = ovRes.data?.overrides || {};
      // Merge saved values over the 0/0 default for every plan row.
      const o = {};
      const sk = {};
      (planRes.data.rows || []).forEach(r => {
        const fromBackend = savedOv[r.product_id];
        o[r.product_id] = {
          'E-302': Number((fromBackend || {})['E-302'] || 0),
          'E-303': Number((fromBackend || {})['E-303'] || 0),
        };
        if (fromBackend) {
          if ((fromBackend['E-302'] || 0) > 0) sk[`${r.product_id}::E-302`] = true;
          if ((fromBackend['E-303'] || 0) > 0) sk[`${r.product_id}::E-303`] = true;
        }
      });
      setOverrides(o);
      setSavedKeys(sk);
      setEditingKeys({});
    } catch {
      if (seq === loadSeqRef.current) toast.error('Failed to load plan');
    }
    finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, groupByParent]);

  // Silent refresh when parent bumps `refreshKey` (e.g., on tab re-entry). Skips
  // the initial render — the [dateFrom, dateTo, groupByParent] effect above
  // already covers first mount. Local UI state (expandedProduct, expandedSub,
  // reservedKeys, lotsByKey, overrides for active editing, sort, scroll) is
  // preserved because we only call load() which writes to `data` state.
  const isFirstPlanningRefreshRef = React.useRef(true);
  useEffect(() => {
    if (isFirstPlanningRefreshRef.current) { isFirstPlanningRefreshRef.current = false; return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Persist a single (product_id × facility) override to the backend. Called
  // on blur of the Produce input. Idempotent upsert on the server.
  const savePlanOverride = async (row, facility) => {
    const ov = overrides[row.product_id] || { 'E-302': 0, 'E-303': 0 };
    const k = `${row.product_id}::${facility}`;
    try {
      await productionAPI.savePlanOverride({
        date: dateTo,
        product_id: row.product_id,
        by_facility: { 'E-302': Number(ov['E-302']) || 0, 'E-303': Number(ov['E-303']) || 0 },
      });
      setSavedKeys(prev => {
        const next = { ...prev };
        // Mark this facility as saved IF its value > 0; otherwise clear it
        // (so the lock state matches "has a real saved value").
        if ((Number(ov[facility]) || 0) > 0) next[k] = true; else delete next[k];
        return next;
      });
      setEditingKeys(prev => { const n = { ...prev }; delete n[k]; return n; });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Initialize default lots=1 for any row/facility where Produce bags > 0 and
  // a batch size is configured. Pure local-state init — no longer fires any
  // sync to the Production Planner.
  React.useEffect(() => {
    if (!data || !data.rows || data.rows.length === 0) return;
    (data.rows || []).forEach(r => {
      ['E-302', 'E-303'].forEach(f => {
        const bags = (overrides[r.product_id] || {})[f] ?? 0;
        const bs = batchSizeFor(r.product_id, f);
        if (!bs || !bags || bags <= 0) return;
        const lotsKey = `${r.product_id}::${f}`;
        setLotsByKey(prev => prev[lotsKey] != null ? prev : { ...prev, [lotsKey]: 1 });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dateTo]);

  // ---- MANUAL planner sync ----
  // Previously each Produce-bags change auto-created lot sheets in the Production
  // Planner. That made the planner flicker on every keystroke. Now the operator
  // explicitly clicks "Send to Production Planner" which runs syncSheets for every
  // row with bags > 0 in one go.
  const [pushingToPlanner, setPushingToPlanner] = useState(false);
  const sendToPlanner = async () => {
    if (!data || !data.rows) return;
    const tasks = [];
    const skipped = [];
    (data.rows || []).forEach(r => {
      ['E-302', 'E-303'].forEach(f => {
        const bags = (overrides[r.product_id] || {})[f] ?? 0;
        if (!bags || bags <= 0) return;
        const bs = batchSizeFor(r.product_id, f);
        if (!bs) {
          // Without a batch size we cannot derive batches → the backend would
          // reject. Surface this clearly to the operator so they configure it
          // in Products and try again.
          skipped.push(`${r.product_name} (${f})`);
          return;
        }
        const lotsKey = `${r.product_id}::${f}`;
        const lots = lotsByKey[lotsKey] ?? 1;
        tasks.push(syncSheets(r, f, lots));
      });
    });
    if (tasks.length === 0 && skipped.length === 0) {
      toast.info('Nothing to send — set Produce bags > 0 on at least one row.');
      return;
    }
    setPushingToPlanner(true);
    try {
      await Promise.all(tasks);
      try { localStorage.setItem('vikas:lastPlannerSyncDate', dateTo); } catch {}
      if (tasks.length > 0) {
        toast.success(`${tasks.length} facility row${tasks.length === 1 ? '' : 's'} sent to Production Planner for ${dateTo}.`, { duration: 4000 });
      }
      if (skipped.length > 0) {
        toast.error(
          `Skipped (no batch size in Products): ${skipped.slice(0, 4).join(', ')}${skipped.length > 4 ? ` +${skipped.length - 4} more` : ''}.`,
          { duration: 7000 }
        );
      }
    } finally { setPushingToPlanner(false); }
  };

  const facilities = data?.facilities || ['E-302', 'E-303'];
  const rows = React.useMemo(() => {
    let list = (data?.rows || []).slice();
    // Product search filter (matches parent name OR any nested sub-brand name).
    const q = productSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        if ((r.product_name || '').toLowerCase().includes(q)) return true;
        return (r.sub_products || []).some(sp => (sp.name || '').toLowerCase().includes(q));
      });
    }
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    const planTotalOf = (r) => {
      const ov = overrides[r.product_id] || {};
      return (Number(ov['E-302']) || 0) + (Number(ov['E-303']) || 0);
    };
    const actualOf = (r) => (r.total_stock || 0) + planTotalOf(r) - (r.ordered_remaining || 0);
    const num = (a, b) => (a - b) * dir;
    list.sort((a, b) => {
      switch (sortBy.key) {
        case 'demand': return num(a.ordered_remaining || 0, b.ordered_remaining || 0);
        case 'total_stock': return num(a.total_stock || 0, b.total_stock || 0);
        case 'shortfall': return num(a.net_required || 0, b.net_required || 0);
        case 'excess': return num(a.excess_stock || 0, b.excess_stock || 0);
        case 'actual': return num(actualOf(a), actualOf(b));
        case 'product_name':
        default:
          return (a.product_name || '').localeCompare(b.product_name || '', undefined, { sensitivity: 'base' }) * dir;
      }
    });
    return list;
  }, [data, sortBy, overrides, productSearch]);
  const { visible: visiblePlanRows, hasMore: planHasMore, remaining: planRemaining, loadMoreRef: planLoadMoreRef, showMore: planShowMore } =
    useWindowedList(rows, { resetDeps: [sortBy, productSearch, dateFrom, dateTo, rows.length] });
  const totals = facilities.reduce((acc, f) => { acc[f] = 0; return acc; }, {});
  rows.forEach(r => facilities.forEach(f => { totals[f] += (overrides[r.product_id]?.[f] ?? 0) || 0; }));
  const grandDemand = rows.reduce((s, r) => s + r.ordered_remaining, 0);
  const grandShortfall = rows.reduce((s, r) => s + r.net_required, 0);  const grandExcess = rows.reduce((s, r) => s + (r.excess_stock || 0), 0);

  // No auto-balance / no auto-fill: each facility's Produce input is independent.
  // The operator types in the actual bags to produce per facility; nothing else changes.
  const setOv = (row, f, v) => {
    const n = Math.max(0, parseInt(v || '0', 10) || 0);
    setOverrides(prev => {
      const cur = prev[row.product_id] || { 'E-302': 0, 'E-303': 0 };
      return { ...prev, [row.product_id]: { ...cur, [f]: n } };
    });
  };

  const reservedQtyFor = (row) => {
    let sum = 0;
    (row.dealers || []).forEach(d => {
      if (reservedKeys[`${row.product_id}::${d.dealer_id}`]) sum += d.quantity;
    });
    return sum;
  };

  // Residual production needed for the unreserved pool, assuming reserved bags consume
  // current stock first. Math:
  //   stock_left_for_others = max(0, stock - reserved)
  //   unreserved_demand     = demand - reserved
  //   residual_for_others   = max(0, unreserved_demand - stock_left_for_others)
  // When reserved <= stock this collapses to the original shortfall (max(0, demand - stock)).
  // When reserved > stock, others need MORE production because stock is fully claimed.
  const residualForOthers = (row) => {
    const reserved = reservedQtyFor(row);
    if (reserved === 0) return row.net_required;
    const stockLeft = Math.max(0, row.total_stock - reserved);
    const unreservedDemand = row.ordered_remaining - reserved;
    return Math.max(0, unreservedDemand - stockLeft);
  };

  const toggleReserve = (productId, dealerId) => {
    const k = `${productId}::${dealerId}`;
    setReservedKeys(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k]; else next[k] = true;
      return next;
    });
  };

  // Compute batches needed for a (row × facility): ceil(produce_bags / batch_size).
  // Returns null when batch size is not configured for that facility.
  const batchesNeeded = (row, facility) => {
    const bags = (overrides[row.product_id] || {})[facility] ?? 0;
    const bs = batchSizeFor(row.product_id, facility);
    if (!bs || !bags) return 0;
    return Math.ceil(Number(bags) / bs);
  };

  // Sync lot sheets to backend for (row × facility): user has changed lots count.
  // The backend creates / trims / adjusts open sheets to match `total_lots`.
  // Closed sheets are preserved. We seed each NEW sheet with rows from the row's
  // sub-brand drill (or a single row for the parent product when there are none).
  const syncSheets = async (row, facility, lots) => {
    const bs = batchSizeFor(row.product_id, facility);
    if (!bs) {
      toast.error(`Batch size for ${facility} not set on ${row.product_name}`);
      return;
    }
    const batches = batchesNeeded(row, facility);
    if (batches <= 0) return;
    // Seed rows from sub_products (so MISC parent shows sub-brands), else single row
    const subs = row.sub_products || [];
    const tpl = subs.length > 0 ? subs.map((sp, idx) => ({
      seq_no: idx + 1,
      sub_brand_name: sp.sub_brand_name,
      weight_kg: 50,
      variant: null,
      batches: 0,
      actual_production: 0,
    })) : [{
      seq_no: 1,
      sub_brand_name: row.product_name,
      weight_kg: null,
      variant: null,
      batches: 0,
      actual_production: 0,
    }];
    try {
      await productionAPI.syncLotSheets({
        facility,
        parent_product_id: row.product_id,
        date: dateTo,
        total_lots: Math.max(1, parseInt(lots || '1', 10)),
        total_batches: batches,
        rows_template: tpl,
      });
      toast.success(`${row.product_name} → ${facility}: ${lots} lot${lots === 1 ? '' : 's'} synced`, { duration: 1500 });
    } catch (e) {
      toast.error(`Sheet sync failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const setLots = (row, facility, raw) => {
    const lots = Math.max(1, parseInt(raw || '1', 10));
    const k = `${row.product_id}::${facility}`;
    setLotsByKey(prev => ({ ...prev, [k]: lots }));
    // No longer auto-syncs to the Production Planner — operator clicks
    // "Send to Production Planner" to push all rows at once.
  };

  // Click a dealer row → show that dealer's outstanding orders for this product.
  // Uses the order_ids the backend returned per dealer to filter the orders prop.
  // When `subBrandFilter` is set, line items shown are restricted to that sub-brand only.
  const openDealerOrders = (row, dealer, subBrandFilter = null) => {
    const ids = new Set(dealer.order_ids || []);
    const matches = (orders || [])
      .filter(o => ids.has(o.id) && o.dealer_id === dealer.dealer_id)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    if (matches.length === 0) {
      toast.error('No matching orders found in current loaded list');
      return;
    }
    setDrillOrders({
      orders: matches,
      product_id: row.product_id,
      product_name: subBrandFilter ? `${row.product_name} — ${subBrandFilter}` : row.product_name,
      dealer_name: dealer.dealer_name,
      sub_brand_filter: subBrandFilter,
    });
  };

  return (
    <div data-testid="planning-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Production Planning</h2>
      </div>

      {/* Filters: date presets + group toggle */}
      <Card className="p-5 bg-white border mb-4" style={{ borderColor: '#E2DFD8' }}>
        <p className="text-xs mb-3" style={{ color: '#5C6B61' }}>Dates refer to the <b>Schedule Date</b> (delivery date) of orders.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Schedule Date</label>
            <div className="flex gap-1.5">
              {[
                { key: 'today', label: 'Today' },
                { key: 'tomorrow', label: 'Tomorrow' },
                { key: 'edit', label: 'Edit' },
              ].map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className="text-xs px-3 py-2 rounded font-medium border transition-colors h-10"
                  style={datePreset === p.key
                    ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
                    : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
                  data-testid={`plan-date-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {datePreset === 'edit' && (
            <>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>From</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" data-testid="plan-from" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>To</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="plan-to" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Search Product</label>
            <div className="relative">
              <Input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search by name…"
                className="w-56 h-10 pr-8"
                data-testid="plan-product-search"
              />
              {productSearch && (
                <button
                  type="button"
                  onClick={() => setProductSearch('')}
                  className="absolute top-1/2 right-2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold leading-none"
                  style={{ color: '#C05C35', background: '#FBF9F6', border: '1px solid #E2DFD8' }}
                  title="Clear search"
                  aria-label="Clear search"
                  data-testid="plan-product-search-clear"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          <label
            className="flex items-center gap-2 text-sm cursor-pointer h-10 px-3 rounded border"
            style={{ borderColor: groupByParent ? '#2B5339' : '#E2DFD8', background: groupByParent ? '#EFF4EE' : 'white' }}
            title="When ON, MISC sub-brand line items roll up under their parent product. The bare MISC sentinel is hidden."
            data-testid="plan-group-by-parent-toggle"
          >
            <input
              type="checkbox"
              checked={groupByParent}
              onChange={(e) => setGroupByParent(e.target.checked)}
              className="cursor-pointer"
              data-testid="plan-group-by-parent-checkbox"
            />
            <span style={{ color: groupByParent ? '#2B5339' : '#5C6B61' }}>Stack MISC under parent</span>
          </label>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Products in demand</div>
          <div className="text-2xl font-bold">{rows.length}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Total bags ordered</div>
          <div className="text-2xl font-bold" style={{ color: '#2B5339' }}>{grandDemand.toLocaleString('en-IN')}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#C05C35' }}>Shortfall after stock</div>
          <div className="text-2xl font-bold" style={{ color: '#C05C35' }}>{grandShortfall.toLocaleString('en-IN')}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#2B5339' }}>Excess stock</div>
          <div className="text-2xl font-bold" style={{ color: '#2B5339' }}>{grandExcess.toLocaleString('en-IN')}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs" style={{ color: '#5C6B61' }}>Plan total</div>
          <div className="text-2xl font-bold" style={{ color: '#2B5339' }}>
            {facilities.reduce((s, f) => s + (totals[f] || 0), 0).toLocaleString('en-IN')}
          </div>
          <div className="text-[10px]" style={{ color: '#5C6B61' }}>
            {facilities.map(f => `${f}: ${(totals[f] || 0).toLocaleString('en-IN')}`).join(' · ')}
          </div>
        </Card>
      </div>

      {/* Manual "Send to Production Planner" action bar — replaces the legacy
          auto-sync that fired on every keystroke. Operator now reviews the plan
          and clicks this once when ready to push lot sheets. */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 p-3 rounded border" style={{ background: '#FFF8F2', borderColor: '#E2DFD8' }}>
        <div className="text-xs" style={{ color: '#5C6B61' }}>
          Set <b>Produce bags</b> and <b>Lots</b> per facility, then click <b>Send to Production Planner</b> to auto-create the lot sheets there. Sheets you've already filled won't be overwritten — sync is idempotent.
        </div>
        <Button
          onClick={sendToPlanner}
          disabled={pushingToPlanner}
          className="text-sm h-9 px-3"
          style={{ background: '#2B5339' }}
          data-testid="plan-send-to-planner"
        >
          {pushingToPlanner ? 'Sending…' : 'Send to Production Planner'}
        </Button>
      </div>

      <Card className="overflow-auto">
        <table className="w-full text-sm" data-testid="planning-table">
          <thead>
            <tr style={{ background: '#FBF9F6' }}>
              <th
                className="p-2 text-left min-w-[280px] cursor-pointer select-none hover:bg-[#F1ECE3]"
                onClick={() => toggleSort('product_name', 'asc')}
                title="Click to sort by product name"
                data-testid="plan-sort-product"
              >Product{sortArrow('product_name')}</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#F1ECE3]"
                onClick={() => toggleSort('demand', 'desc')}
                title="Click to sort by demand"
                data-testid="plan-sort-demand"
              >Demand (rem.){sortArrow('demand')}</th>
              <th className="p-2 text-right">Stock E-302</th>
              <th className="p-2 text-right">Stock E-303</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#F1ECE3]"
                onClick={() => toggleSort('total_stock', 'desc')}
                title="Total stock = physical stock across facilities − in-transit (dispatched but not delivered). Click to sort."
                data-testid="plan-sort-total-stock"
              >Total stock{sortArrow('total_stock')}</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#F1ECE3]"
                style={{ color: '#C05C35' }}
                onClick={() => toggleSort('shortfall', 'desc')}
                title="Click to sort by shortfall (urgent first)"
                data-testid="plan-sort-shortfall"
              >Shortfall{sortArrow('shortfall')}</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#F1ECE3]"
                style={{ color: '#2B5339' }}
                onClick={() => toggleSort('excess', 'desc')}
                title="Click to sort by excess stock"
                data-testid="plan-sort-excess"
              >Excess{sortArrow('excess')}</th>
              <th className="p-2 text-right">Produce E-302</th>
              <th className="p-2 text-right">Produce E-303</th>
              <th
                className="p-2 text-right cursor-pointer select-none hover:bg-[#F1ECE3]"
                onClick={() => toggleSort('actual', 'asc')}
                title="Projected stock position after planned production runs. Click to sort (shortfalls first when ascending)."
                data-testid="plan-sort-actual"
              >Actual Excess/Shortfall{sortArrow('actual')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="10" className="p-8 text-center" style={{ color: '#5C6B61' }}>No outstanding demand for this range.</td></tr>
            ) : visiblePlanRows.map(r => {
              const ov = overrides[r.product_id] || {};
              const planTotal = (ov['E-302'] || 0) + (ov['E-303'] || 0);
              const warn = r.net_required > 0 && planTotal < r.net_required;
              const isOpen = expandedProduct === r.product_id;
              const dealers = r.dealers || [];
              const directDealers = r.direct_dealers || [];
              const subProducts = r.sub_products || [];
              const reservedQty = reservedQtyFor(r);
              const residual = residualForOthers(r);
              return (
                <React.Fragment key={r.product_id}>
                  <tr
                    className="border-t hover:bg-[#FBF9F6] transition-colors"
                    style={{ borderColor: '#E2DFD8', cursor: dealers.length > 0 ? 'pointer' : 'default', borderBottom: isOpen ? 'none' : undefined }}
                    onClick={() => { if (dealers.length > 0) setExpandedProduct(isOpen ? null : r.product_id); }}
                    data-testid="planning-row"
                  >
                    <td className="p-2 font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        {dealers.length > 0 && (
                          <span className="transition-transform" style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', fontSize: '10px', color: '#5C6B61' }}>&#9654;</span>
                        )}
                        {r.product_name}
                        {dealers.length > 0 && (
                          <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                            {dealers.length} dealer{dealers.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {reservedQty > 0 && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                            style={{ background: '#FFF1E8', color: '#C05C35', border: '1px solid #E89D4F' }}
                            title={`${reservedQty} bag${reservedQty !== 1 ? 's' : ''} reserved · residual production for others: ${residual}`}
                            data-testid="plan-reserve-chip"
                          >
                            🔒 Reserved {reservedQty} → others need {residual}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px]" style={{ color: '#5C6B61' }}>{r.order_count} orders</div>
                    </td>
                    <td className="p-2 text-right font-semibold">{r.ordered_remaining.toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right" style={{ color: '#5C6B61' }}>{(r.stock_by_facility['E-302'] || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right" style={{ color: '#5C6B61' }}>{(r.stock_by_facility['E-303'] || 0).toLocaleString('en-IN')}</td>
                    <td className="p-2 text-right" data-testid="plan-total-stock">
                      <div className="font-semibold">{r.total_stock.toLocaleString('en-IN')}</div>
                      {(r.in_transit || 0) > 0 && (
                        <div
                          className="text-[10px] font-normal"
                          style={{ color: '#C05C35' }}
                          title={`Physical stock ${(r.physical_stock || 0).toLocaleString('en-IN')} − in-transit ${(r.in_transit || 0).toLocaleString('en-IN')} = available ${r.total_stock.toLocaleString('en-IN')}`}
                        >
                          {(r.physical_stock || 0).toLocaleString('en-IN')} − {(r.in_transit || 0).toLocaleString('en-IN')} in transit
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-right font-bold" style={{ color: r.net_required > 0 ? '#C05C35' : '#5C6B61' }}>
                      {r.net_required > 0 ? r.net_required.toLocaleString('en-IN') : '-'}
                    </td>
                    <td className="p-2 text-right font-bold" style={{ color: (r.excess_stock || 0) > 0 ? '#2B5339' : '#5C6B61' }} data-testid="plan-excess-stock">
                      {(r.excess_stock || 0) > 0 ? r.excess_stock.toLocaleString('en-IN') : '-'}
                    </td>
                    <td className="p-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const bs = batchSizeFor(r.product_id, 'E-302');
                        const batches = batchesNeeded(r, 'E-302');
                        const lotsKey = `${r.product_id}::E-302`;
                        const lots = lotsByKey[lotsKey] ?? 1;
                        const k = `${r.product_id}::E-302`;
                        const isLocked = !!savedKeys[k] && !editingKeys[k];
                        return (
                          <div className="flex flex-col items-end gap-1" data-testid="plan-produce-E-302">
                            {isLocked ? (
                              <div className="flex items-center gap-1.5 h-8">
                                <span
                                  className="font-semibold text-sm px-2 py-1 rounded border"
                                  style={{ background: '#EFF4EE', color: '#2B5339', borderColor: '#2B5339' }}
                                  title="Saved. Click Edit to modify."
                                >
                                  {(ov['E-302'] || 0).toLocaleString('en-IN')}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setEditingKeys(prev => ({ ...prev, [k]: true }))}
                                  className="text-[10px] px-1.5 py-0.5 rounded border font-medium hover:bg-[#FBF9F6]"
                                  style={{ color: '#2B5339', borderColor: '#2B5339' }}
                                  title="Edit saved value"
                                  data-testid="plan-edit-E-302"
                                >Edit</button>
                              </div>
                            ) : (
                              <Input
                                type="number"
                                min="0"
                                value={ov['E-302'] ?? 0}
                                onChange={(e) => setOv(r, 'E-302', e.target.value)}
                                onBlur={() => savePlanOverride(r, 'E-302')}
                                className="h-8 w-24 text-right"
                                data-testid="plan-E-302"
                              />
                            )}
                            <div className="text-[10px] flex items-center gap-1" style={{ color: '#5C6B61' }}>
                              <span title={bs ? `Batch: ${bs} bags` : 'Set batch size in Products'}>{bs ? `${batches} batch${batches !== 1 ? 'es' : ''}` : 'no batch size'}</span>
                            </div>
                            {bs && batches > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px]" style={{ color: '#5C6B61' }}>Lots:</span>
                                <Input type="number" min="1" value={lots} onChange={(e) => setLots(r, 'E-302', e.target.value)} className="h-6 w-12 text-right text-xs" data-testid="plan-lots-E-302" />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {(() => {
                        const bs = batchSizeFor(r.product_id, 'E-303');
                        const batches = batchesNeeded(r, 'E-303');
                        const lotsKey = `${r.product_id}::E-303`;
                        const lots = lotsByKey[lotsKey] ?? 1;
                        const k = `${r.product_id}::E-303`;
                        const isLocked = !!savedKeys[k] && !editingKeys[k];
                        return (
                          <div className="flex flex-col items-end gap-1" data-testid="plan-produce-E-303">
                            <div className="flex items-center gap-1">
                              {isLocked ? (
                                <div className="flex items-center gap-1.5 h-8">
                                  <span
                                    className="font-semibold text-sm px-2 py-1 rounded border"
                                    style={{ background: '#EFF4EE', color: '#2B5339', borderColor: '#2B5339' }}
                                    title="Saved. Click Edit to modify."
                                  >
                                    {(ov['E-303'] || 0).toLocaleString('en-IN')}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setEditingKeys(prev => ({ ...prev, [k]: true }))}
                                    className="text-[10px] px-1.5 py-0.5 rounded border font-medium hover:bg-[#FBF9F6]"
                                    style={{ color: '#2B5339', borderColor: '#2B5339' }}
                                    title="Edit saved value"
                                    data-testid="plan-edit-E-303"
                                  >Edit</button>
                                </div>
                              ) : (
                                <Input
                                  type="number"
                                  min="0"
                                  value={ov['E-303'] ?? 0}
                                  onChange={(e) => setOv(r, 'E-303', e.target.value)}
                                  onBlur={() => savePlanOverride(r, 'E-303')}
                                  className="h-8 w-24 text-right"
                                  data-testid="plan-E-303"
                                />
                              )}
                              {warn && <span title="Plan total is less than shortfall" style={{ color: '#C05C35' }}>!</span>}
                            </div>
                            <div className="text-[10px]" style={{ color: '#5C6B61' }}>
                              <span title={bs ? `Batch: ${bs} bags` : 'Set batch size in Products'}>{bs ? `${batches} batch${batches !== 1 ? 'es' : ''}` : 'no batch size'}</span>
                            </div>
                            {bs && batches > 0 && (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px]" style={{ color: '#5C6B61' }}>Lots:</span>
                                <Input type="number" min="1" value={lots} onChange={(e) => setLots(r, 'E-303', e.target.value)} className="h-6 w-12 text-right text-xs" data-testid="plan-lots-E-303" />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-2 text-right font-bold" data-testid="plan-actual-excess-shortfall">
                      {(() => {
                        // Projected stock position once the planned production is run:
                        //   net = total_stock + (E-302 + E-303 plan) - ordered_remaining
                        // > 0 → leftover stock after fulfilling all orders (green excess)
                        // < 0 → still short by |net| bags (red shortfall)
                        // = 0 → perfectly balanced (neutral)
                        const net = (r.total_stock || 0) + planTotal - (r.ordered_remaining || 0);
                        if (net > 0) {
                          return (
                            <span style={{ color: '#2B5339' }} title="Projected leftover stock after planned production runs">
                              +{net.toLocaleString('en-IN')}
                              <div className="text-[10px] font-normal" style={{ color: '#5C6B61' }}>excess</div>
                            </span>
                          );
                        }
                        if (net < 0) {
                          return (
                            <span style={{ color: '#C05C35' }} title="Projected shortfall after planned production runs">
                              −{Math.abs(net).toLocaleString('en-IN')}
                              <div className="text-[10px] font-normal" style={{ color: '#5C6B61' }}>shortfall</div>
                            </span>
                          );
                        }
                        return (
                          <span style={{ color: '#5C6B61' }} title="Plan exactly meets demand">
                            0
                            <div className="text-[10px] font-normal">balanced</div>
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                  {isOpen && dealers.length > 0 && (
                    <tr data-testid="plan-dealer-details">
                      <td colSpan="10" className="p-0" style={{ background: '#FBF9F6' }}>
                        <div className="mx-3 my-2 rounded border" style={{ borderColor: '#E2DFD8', background: '#FFFFFF' }}>
                          {/* SUB-BRAND STACK (only when stacking ON and parent has sub-brand demand) */}
                          {subProducts.length > 0 && (
                            <>
                              <div className="p-2 border-b" style={{ borderColor: '#E2DFD8', background: '#FFF8F2' }}>
                                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#C05C35' }} data-testid="plan-sub-products-header">
                                  Sub-Brands stacked under {r.product_name}
                                </p>
                              </div>
                              <table className="w-full">
                                <thead>
                                  <tr style={{ background: '#FFF1E8' }}>
                                    <th className="text-left p-2 pl-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Sub-Brand</th>
                                    <th className="text-right p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}>Demand (rem.)</th>
                                    <th className="text-center p-2 pr-4 text-xs font-semibold" style={{ color: '#5C6B61' }}># Orders</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {subProducts.map((sp) => {
                                    const sk = `${r.product_id}::${sp.sub_brand_name}`;
                                    const subOpen = expandedSub === sk;
                                    return (
                                      <React.Fragment key={sp.sub_brand_name}>
                                        <tr
                                          style={{ borderBottom: '1px solid #E2DFD8', cursor: 'pointer', background: subOpen ? '#FFF8F2' : undefined }}
                                          className="hover:bg-[#FFF8F2] transition-colors"
                                          onClick={() => setExpandedSub(subOpen ? null : sk)}
                                          data-testid="plan-sub-product-row"
                                        >
                                          <td className="p-2 pl-4 text-sm font-medium">
                                            <span className="transition-transform mr-1" style={{ transform: subOpen ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', fontSize: '10px', color: '#5C6B61' }}>&#9654;</span>
                                            {sp.sub_brand_name}
                                            <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded" style={{ background: '#FBF9F6', color: '#5C6B61' }}>
                                              {sp.dealers.length} dealer{sp.dealers.length !== 1 ? 's' : ''}
                                            </span>
                                          </td>
                                          <td className="p-2 text-sm text-right font-bold" style={{ color: '#C05C35' }}>{sp.quantity.toLocaleString('en-IN')}</td>
                                          <td className="p-2 pr-4 text-sm text-center" style={{ color: '#5C6B61' }}>{sp.order_count}</td>
                                        </tr>
                                        {subOpen && (
                                          <tr style={{ background: '#FFF8F2' }}>
                                            <td colSpan="3" className="p-0">
                                              <table className="w-full">
                                                <thead>
                                                  <tr style={{ background: '#f5f1e8' }}>
                                                    <th className="text-left p-2 pl-8 text-[11px] font-semibold" style={{ color: '#5C6B61' }}>Dealer (click to view orders)</th>
                                                    <th className="text-right p-2 text-[11px] font-semibold" style={{ color: '#5C6B61' }}>Demand (rem.)</th>
                                                    <th className="text-center p-2 pr-4 text-[11px] font-semibold" style={{ color: '#5C6B61' }}># Orders</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {sp.dealers.map((de) => (
                                                    <tr
                                                      key={de.dealer_id}
                                                      style={{ borderBottom: '1px solid #E2DFD8', cursor: 'pointer' }}
                                                      className="hover:bg-[#FFF1E8] transition-colors"
                                                      onClick={() => openDealerOrders(r, de, sp.sub_brand_name)}
                                                      data-testid="plan-sub-dealer-row"
                                                    >
                                                      <td className="p-2 pl-8 text-sm">{de.dealer_name}</td>
                                                      <td className="p-2 text-sm text-right font-medium" style={{ color: '#2B5339' }}>{de.quantity.toLocaleString('en-IN')}</td>
                                                      <td className="p-2 pr-4 text-sm text-center" style={{ color: '#5C6B61' }}>{de.order_count}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </>
                          )}

                          {/* DIRECT DEALER BREAKDOWN (parent product ordered directly, no sub-brand) */}
                          <div className="p-2 border-b border-t flex items-center justify-between flex-wrap gap-2" style={{ borderColor: '#E2DFD8' }}>
                            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#5C6B61' }}>
                              {subProducts.length > 0 ? `Direct Orders for ${r.product_name}` : `Dealer-wise Breakdown for ${r.product_name}`}
                            </p>
                            <p className="text-[10px]" style={{ color: '#5C6B61' }}>
                              Tick <b style={{ color: '#C05C35' }}>Reserve</b> to lock that dealer's qty as must-fulfil.
                            </p>
                          </div>
                          {directDealers.length === 0 ? (
                            <div className="p-3 pl-4 text-xs italic" style={{ color: '#5C6B61' }}>
                              No direct {r.product_name} orders in this period — only sub-brand orders (see above).
                            </div>
                          ) : (
                            <table className="w-full">
                              <thead>
                                <tr style={{ background: '#f5f1e8' }}>
                                  <th className="text-left p-2 pl-4 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dealer (click to view orders)</th>
                                  <th className="text-right p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}>Demand (rem.)</th>
                                  <th className="text-center p-2 text-xs font-semibold" style={{ color: '#5C6B61' }}># Orders</th>
                                  <th className="text-center p-2 pr-4 text-xs font-semibold" style={{ color: '#C05C35' }}>Reserve</th>
                                </tr>
                              </thead>
                              <tbody>
                                {directDealers.map((de) => {
                                  const k = `${r.product_id}::${de.dealer_id}`;
                                  const isReserved = !!reservedKeys[k];
                                  return (
                                    <tr
                                      key={de.dealer_id}
                                      style={{ borderBottom: '1px solid #E2DFD8', background: isReserved ? '#FFF8F2' : undefined, cursor: 'pointer' }}
                                      className="hover:bg-[#f5f1e8] transition-colors"
                                      onClick={() => openDealerOrders(r, de)}
                                      data-testid="plan-dealer-row"
                                    >
                                      <td className="p-2 pl-4 text-sm font-medium" style={{ color: isReserved ? '#C05C35' : undefined }}>
                                        {isReserved && <span className="mr-1" title="Reserved (must-fulfil)">🔒</span>}
                                        {de.dealer_name}
                                      </td>
                                      <td className="p-2 text-sm text-right font-medium" style={{ color: isReserved ? '#C05C35' : '#2B5339' }}>{de.quantity.toLocaleString('en-IN')}</td>
                                      <td className="p-2 text-sm text-center" style={{ color: '#5C6B61' }}>{de.order_count}</td>
                                      <td className="p-2 pr-4 text-center" onClick={(e) => e.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={isReserved}
                                          onChange={() => toggleReserve(r.product_id, de.dealer_id)}
                                          className="cursor-pointer w-4 h-4"
                                          title="Reserve this dealer's qty as must-fulfil"
                                          data-testid="plan-dealer-reserve-checkbox"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {planHasMore && (
              <tr ref={planLoadMoreRef} data-testid="planning-load-more">
                <td colSpan="10" className="p-3 text-center" style={{ background: '#FBF9F6' }}>
                  <Button variant="outline" size="sm" onClick={planShowMore} data-testid="planning-show-more-btn">
                    Show more ({planRemaining} more)
                  </Button>
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr style={{ background: '#FBF9F6' }}>
                <td colSpan="5" className="p-2 font-bold">TOTAL</td>
                <td className="p-2 text-right font-bold" style={{ color: '#C05C35' }}>{grandShortfall.toLocaleString('en-IN')}</td>
                <td className="p-2 text-right font-bold" style={{ color: '#2B5339' }}>{grandExcess.toLocaleString('en-IN')}</td>
                <td className="p-2 text-right font-bold">{totals['E-302'].toLocaleString('en-IN')}</td>
                <td className="p-2 text-right font-bold">{totals['E-303'].toLocaleString('en-IN')}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
      <div className="text-[11px] mt-2" style={{ color: '#5C6B61' }}>
        Demand = ordered bags minus already-dispatched qty across all splits, for orders with delivery date in range. Each facility's Produce input is independent and is <b>auto-saved on blur</b>; saved values lock with an Edit button. Values persist across reloads and date changes.
      </div>

      {/* Multi-order drill dialog — lists orders for a dealer × product (mirrors Product Summary) */}
      <Dialog open={!!drillOrders} onOpenChange={(o) => { if (!o) setDrillOrders(null); }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="plan-drill-orders-dialog">
          <DialogHeader>
            <DialogTitle>
              {drillOrders?.dealer_name} — {drillOrders?.product_name}
              <span className="ml-2 text-sm font-normal" style={{ color: '#5C6B61' }}>
                ({(drillOrders?.orders || []).length} order{(drillOrders?.orders || []).length !== 1 ? 's' : ''})
              </span>
            </DialogTitle>
          </DialogHeader>
          {drillOrders && (
            <div className="space-y-3">
              {drillOrders.orders.map((o) => {
                // Show line items belonging to this product (parent or raw match — supports stacking).
                // When `sub_brand_filter` is set, narrow further to just the items matching that sub-brand name.
                const baseItems = (o.items || []).filter(i =>
                  i.product_id === drillOrders.product_id || i.parent_product_id === drillOrders.product_id
                );
                const prodItems = drillOrders.sub_brand_filter
                  ? baseItems.filter(i => (i.product_name || '') === drillOrders.sub_brand_filter)
                  : baseItems;
                const otherItems = (o.items || []).filter(i => !prodItems.includes(i));
                const statusColor = (o.status === 'dispatched' || o.status === 'delivered')
                  ? '#C05C35'
                  : (o.status === 'confirmed' ? '#2B5339' : '#E89D4F');
                return (
                  <div key={o.id} className="border rounded p-3" style={{ borderColor: '#E2DFD8' }} data-testid="plan-drill-order-card">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="text-xs font-bold" style={{ color: '#5C6B61' }}>Order #{o.id.slice(0, 8)}</div>
                        <div className="text-xs" style={{ color: '#5C6B61' }}>Ordered: {fmtDate(o.created_at)}</div>
                      </div>
                      <span className="text-xs font-bold uppercase px-2 py-1 rounded" style={{ background: statusColor, color: 'white' }}>
                        {o.status}
                      </span>
                    </div>
                    {prodItems.map((it, pi) => (
                      <div key={pi} className="flex items-center justify-between p-2 text-sm rounded mb-1" style={{ background: '#FBF9F6' }}>
                        <span className="font-medium">
                          {prodItems.length > 1 && <span className="mr-1 text-xs" style={{ color: '#5C6B61' }}>#{pi + 1}</span>}
                          {it.product_name}
                        </span>
                        <span style={{ color: '#5C6B61' }}>
                          {it.quantity} {'\u00D7'} {'\u20B9'}{it.price} = <b style={{ color: '#2B5339' }}>{'\u20B9'}{it.total}</b>
                        </span>
                      </div>
                    ))}
                    {otherItems.map((it, j) => (
                      <div key={j} className="flex items-center justify-between px-2 py-1 text-xs mt-1" style={{ color: '#5C6B61' }}>
                        <span>+ {it.product_name}</span>
                        <span>{it.quantity} {'\u00D7'} {'\u20B9'}{it.price} = {'\u20B9'}{it.total}</span>
                      </div>
                    ))}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                      {o.delivery_date && (
                        <div>
                          <div style={{ color: '#5C6B61' }}>Schedule</div>
                          <div className="font-medium">{fmtDate(o.delivery_date)}{o.delivery_time ? ' ' + o.delivery_time : ''}</div>
                        </div>
                      )}
                      {o.vehicle_number && (
                        <>
                          <div>
                            <div style={{ color: '#5C6B61' }}>Vehicle</div>
                            <div className="font-medium">{o.vehicle_number}</div>
                          </div>
                          <div>
                            <div style={{ color: '#5C6B61' }}>Driver</div>
                            <div className="font-medium">{o.driver_mobile || '-'}</div>
                          </div>
                        </>
                      )}
                      {(() => {
                        const t = computeOrderTotals(o);
                        return (
                          <div className="text-right col-span-2 md:col-span-1" data-testid="plan-drill-order-totals">
                            <div className="flex justify-between md:block">
                              <span style={{ color: '#5C6B61' }}>Gross</span>
                              <span className="md:block font-medium">{'\u20B9'}{t.gross.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between md:block mt-0.5">
                              <span style={{ color: '#C05C35' }}>Discount ({t.blendedPct.toFixed(2)}%)</span>
                              <span className="md:block font-medium" style={{ color: '#C05C35' }}>- {'\u20B9'}{t.discountAmt.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between md:block mt-0.5 pt-0.5 border-t" style={{ borderColor: '#E2DFD8' }}>
                              <span style={{ color: '#5C6B61' }}>Net</span>
                              <span className="md:block font-bold" style={{ color: '#2B5339' }}>{'\u20B9'}{t.net.toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {o.manual_description && (
                      <div className="mt-2 text-xs p-2 rounded" style={{ background: '#FBF9F6', color: '#C05C35', whiteSpace: 'pre-wrap' }}>
                        {o.manual_description}
                      </div>
                    )}
                    {/* Edit Order — bottom-left of each order card. Hands off
                        to OrdersTab via the `onEditOrder` callback so the
                        operator can fix qty/items without leaving the
                        Planning context manually. We also pass the CURRENT
                        drill context so AdminDashboard can restore this very
                        popup when the edit dialog closes (user spec: "always
                        de-escalate one step only"). */}
                    {onEditOrder && (o.status === 'pending' || o.status === 'confirmed') && (
                      <div className="flex justify-start mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const ctx = drillOrders ? {
                              dealer_id: drillOrders.orders?.[0]?.dealer_id,
                              dealer_name: drillOrders.dealer_name,
                              product_id: drillOrders.product_id,
                              product_name: drillOrders.product_name,
                              sub_brand_filter: drillOrders.sub_brand_filter,
                            } : null;
                            setDrillOrders(null);
                            if (typeof onEditOrder === 'function') onEditOrder(o.id, ctx);
                          }}
                          data-testid="plan-drill-edit-order-btn"
                          style={{ borderColor: '#2B5339', color: '#2B5339' }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Order
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

